import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, InputAdornment, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, MenuItem, Snackbar, Alert,
  CircularProgress, IconButton, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { hasPermission } from '../common/permissions';
import api from '../../services/api';
import DepartmentSelect, { useDepartments } from './components/DepartmentSelect';

const EMPLOYMENT_STATUSES = ['PERMANENT', 'CONTRACT', 'CASUAL', 'PART-TIME'];
const SOURCES = ['Machines & Tools', 'Human Factors', 'Environment', 'Chemical/Biological', 'Fire/Explosion', 'Fall', 'Other'];
const MEDICAL_TYPES = ['Lost Time Injury', 'Medical Treatment', 'First Aid', 'Near Miss', 'No Treatment'];

const TEMPLATE_HEADERS = [
  'I.O.D NO', 'NAME OF THE INJURED', 'DAY OF THE WEEK', 'DATE OF INJURY',
  'TIME OF INJURY', 'AGE', 'DESIGNATION', 'EMPLOYMENT STATUS',
  'NSSA CLAIM NUMBER', 'DESCRIPTION OF EVENTS', 'DEPARTMENT OF INJURED',
  'MANAGER /SUPERVISOR', 'SOURCE OF INJURY', 'LOCATION /WORK AREA',
  'PART OF BODY INJURED', 'NATURE OF INJURY', 'DAYS LOST',
  'MEDICAL TREATMENT', 'CORRECTIVE ACTION TAKEN OR RECOMMENDED'
];

const emptyForm = {
  name_of_injured: '', day_of_week: '', date_of_injury: '', time_of_injury: '',
  age: '', designation: '', employment_status: 'PERMANENT', nssa_claim_number: '',
  description_of_events: '', department: '', department_id: '', sub_department_id: '', manager_supervisor: '',
  source_of_injury: '', location_work_area: '', part_of_body_injured: '',
  nature_of_injury: '', days_lost: '', medical_treatment: 'Medical Treatment',
  corrective_action: ''
};

const fieldSx = {
  '& .MuiOutlinedInput-root': { color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' } },
  '& .MuiInputLabel-root': { color: '#94a3b8' },
  '& .MuiSelect-select': { color: 'white' },
  '& .MuiSelect-icon': { color: '#94a3b8' },
  '& .MuiMenuItem-root': { color: 'white' }
};

const selectMenuProps = { PaperProps: { sx: { background: '#1e293b', color: 'white', maxHeight: 320, '& .MuiMenuItem-root:hover': { background: 'rgba(255,255,255,0.08)' }, '& .MuiMenuItem-root.Mui-disabled': { opacity: 1, color: '#64748b' } } } };

const SHEAccidentRegister = ({ user }) => {
  const departments = useDepartments();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/she-accidents');
      setRecords(res.data);
    } catch (e) {
      setNotice({ open: true, message: 'Failed to load records.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => { setEditTarget(null); setForm(emptyForm); setErrors({}); setDialogOpen(true); };
  const openEdit = (rec) => { setEditTarget(rec); setForm({ ...emptyForm, ...rec }); setErrors({}); setDialogOpen(true); };

  const isMeaningful = (val, min = 3) => {
    const v = (val || '').trim();
    if (v.length < min) return false;
    if (/^[^a-zA-Z0-9]+$/.test(v)) return false;
    return true;
  };

  const validate = () => {
    const e = {};
    if (!form.name_of_injured || !isMeaningful(form.name_of_injured, 2))
      e.name_of_injured = 'Enter a valid name (min 2 meaningful characters).';
    if (!form.date_of_injury)
      e.date_of_injury = 'Date of injury is required.';
    if (!form.designation || !isMeaningful(form.designation, 2))
      e.designation = 'Enter a valid designation.';
    if (!form.department || !isMeaningful(form.department, 2))
      e.department = 'Enter a valid department.';
    if (!form.nature_of_injury || !isMeaningful(form.nature_of_injury, 3))
      e.nature_of_injury = 'Describe the nature of injury (min 3 characters).';
    if (form.corrective_action && !isMeaningful(form.corrective_action, 5))
      e.corrective_action = 'Corrective action must be meaningful (min 5 characters).';
    if (form.description_of_events && !isMeaningful(form.description_of_events, 5))
      e.description_of_events = 'Description must be meaningful (min 5 characters).';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editTarget) {
        await api.put(`/she-accidents/${editTarget.id}`, form);
        setNotice({ open: true, message: 'Record updated.', severity: 'success' });
      } else {
        await api.post('/she-accidents', form);
        setNotice({ open: true, message: 'Record created.', severity: 'success' });
      }
      setDialogOpen(false);
      setErrors({});
      fetchData();
    } catch (e) {
      setNotice({ open: true, message: e.response?.data?.message || 'Failed to save.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/she-accidents/${deleteTarget.id}`);
      setNotice({ open: true, message: 'Record deleted.', severity: 'success' });
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      setNotice({ open: true, message: e.response?.data?.message || 'Failed to delete.', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const downloadTemplate = () => {
    const csv = TEMPLATE_HEADERS.join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'SHE_Accident_Register_Template.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/she-accidents/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNotice({ open: true, message: res.data.message, severity: 'success' });
      fetchData();
    } catch (e) {
      setNotice({ open: true, message: e.response?.data?.message || 'Import failed.', severity: 'error' });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getMedicalChip = (type) => {
    const map = {
      'Lost Time Injury': { color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
      'Medical Treatment': { color: '#f97316', bg: 'rgba(249,115,22,0.1)' },
      'First Aid': { color: '#eab308', bg: 'rgba(234,179,8,0.1)' },
      'Near Miss': { color: '#a78bfa', bg: 'rgba(167,139,250,0.1)' },
    };
    const s = map[type] || { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' };
    return <Chip label={type || 'N/A'} size="small" sx={{ color: s.color, background: s.bg, fontWeight: 'bold', fontSize: '0.7rem' }} />;
  };

  const filtered = records.filter(r =>
    [r.iod_number, r.name_of_injured, r.department, r.nature_of_injury, r.designation]
      .some(v => (v || '').toLowerCase().includes(search.toLowerCase()))
  );

  const canAdd = hasPermission(user, 'SHE Compliance', 'add');
  const canEdit = hasPermission(user, 'SHE Compliance', 'edit');
  const canDelete = hasPermission(user, 'SHE Compliance', 'delete');

  return (
    <Box sx={{ p: 3 }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search records..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 220, ...fieldSx }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }}
        />
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={downloadTemplate}
            sx={{ textTransform: 'none', borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}>
            Download Template
          </Button>
          {canAdd && (
            <Button variant="outlined" size="small" startIcon={importing ? null : <FileUploadIcon />} onClick={() => fileInputRef.current?.click()} disabled={importing}
              sx={{ textTransform: 'none', borderColor: '#f59e0b', color: '#f59e0b',
                '&.Mui-disabled': { borderColor: '#f59e0b', color: '#f59e0b', opacity: 0.8 } }}>
              {importing ? 'Importing...' : 'Import CSV/Excel'}
            </Button>
          )}
          {canAdd && (
            <Button variant="contained" size="small" onClick={openAdd}
              sx={{ textTransform: 'none', bgcolor: '#3b82f6', fontWeight: 'bold' }}>
              Record Accident
            </Button>
          )}
        </Box>
        <input type="file" accept=".csv,.xlsx,.xls" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImport} />
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Records', value: records.length, color: '#3b82f6' },
          { label: 'Lost Time Injuries', value: records.filter(r => r.medical_treatment === 'Lost Time Injury').length, color: '#ef4444' },
          { label: 'Medical Treatment', value: records.filter(r => r.medical_treatment === 'Medical Treatment').length, color: '#f97316' },
          { label: 'First Aid', value: records.filter(r => r.medical_treatment === 'First Aid').length, color: '#eab308' },
        ].map(s => (
          <Box key={s.label} sx={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', p: 2, minWidth: 130, border: `1px solid ${s.color}30` }}>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', mb: 0.5 }}>{s.label}</Typography>
            <Typography sx={{ color: s.color, fontSize: '1.6rem', fontWeight: 'bold' }}>{s.value}</Typography>
          </Box>
        ))}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['IOD No.', 'Name', 'Date', 'Designation', 'Department', 'Source', 'Body Part', 'Nature', 'Days Lost', 'Treatment', 'Actions'].map(h => (
                <TableCell key={h} sx={{ color: '#94a3b8', fontWeight: 'bold', background: '#1e293b', whiteSpace: 'nowrap',
                  ...(h === 'Actions' ? { position: 'sticky', right: 0, zIndex: 10, borderLeft: '1px solid rgba(255,255,255,0.1)' } : {}) }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={11} align="center" sx={{ py: 6, color: '#94a3b8' }}>No accident records found.</TableCell></TableRow>
            ) : filtered.map(rec => (
              <TableRow key={rec.id} sx={{ '&:hover': { background: 'rgba(255,255,255,0.03)' } }}>
                <TableCell sx={{ color: '#60a5fa', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{rec.iod_number}</TableCell>
                <TableCell sx={{ color: 'white', whiteSpace: 'nowrap' }}>{rec.name_of_injured || '—'}</TableCell>
                <TableCell sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>{rec.date_of_injury || '—'}</TableCell>
                <TableCell sx={{ color: '#94a3b8' }}>{rec.designation || '—'}</TableCell>
                <TableCell sx={{ color: '#94a3b8' }}>{rec.department || '—'}</TableCell>
                <TableCell sx={{ color: '#94a3b8' }}>{rec.source_of_injury || '—'}</TableCell>
                <TableCell sx={{ color: '#94a3b8' }}>{rec.part_of_body_injured || '—'}</TableCell>
                <TableCell sx={{ color: '#94a3b8' }}>{rec.nature_of_injury || '—'}</TableCell>
                <TableCell sx={{ color: '#94a3b8', textAlign: 'center' }}>{rec.days_lost || '0'}</TableCell>
                <TableCell>{getMedicalChip(rec.medical_treatment)}</TableCell>
                <TableCell sx={{ position: 'sticky', right: 0, background: '#1e293b', borderLeft: '1px solid rgba(255,255,255,0.1)', zIndex: 5 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {canEdit && (
                      <Button variant="outlined" size="small" onClick={() => openEdit(rec)}
                        sx={{ textTransform: 'none', color: '#60a5fa', borderColor: 'rgba(96,165,250,0.4)', fontSize: '0.75rem' }}>
                        Edit
                      </Button>
                    )}
                    {canDelete && (
                      <Button variant="outlined" size="small" onClick={() => setDeleteTarget(rec)}
                        sx={{ textTransform: 'none', color: '#ef4444', borderColor: 'rgba(239,68,68,0.4)', fontSize: '0.75rem' }}>
                        Delete
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { background: '#1e293b', color: 'white', borderRadius: '18px' } }}>
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
          {editTarget ? `Edit — ${editTarget.iod_number}` : 'Record New Accident'}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Name of Injured" value={form.name_of_injured} onChange={e => setForm({ ...form, name_of_injured: e.target.value })} error={!!errors.name_of_injured} helperText={errors.name_of_injured} FormHelperTextProps={{ sx: { color: '#f87171' } }} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Date of Injury" type="date" value={form.date_of_injury} onChange={e => setForm({ ...form, date_of_injury: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ max: new Date().toISOString().split('T')[0] }} error={!!errors.date_of_injury} helperText={errors.date_of_injury} FormHelperTextProps={{ sx: { color: '#f87171' } }} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Day of Week" value={form.day_of_week} onChange={e => setForm({ ...form, day_of_week: e.target.value })} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Time of Injury" type="time" value={form.time_of_injury} onChange={e => setForm({ ...form, time_of_injury: e.target.value })} InputLabelProps={{ shrink: true }} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Age" value={form.age} onChange={e => setForm({ ...form, age: e.target.value })} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Designation" value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })} error={!!errors.designation} helperText={errors.designation} FormHelperTextProps={{ sx: { color: '#f87171' } }} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Employment Status" value={form.employment_status} onChange={e => setForm({ ...form, employment_status: e.target.value })} sx={fieldSx} SelectProps={{ MenuProps: selectMenuProps }}>
                {EMPLOYMENT_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="NSSA Claim Number" value={form.nssa_claim_number} onChange={e => setForm({ ...form, nssa_claim_number: e.target.value })} sx={fieldSx} />
            </Grid>
            <DepartmentSelect
              departmentId={form.department_id}
              subDepartmentId={form.sub_department_id}
              onDepartmentChange={(id, name) => setForm(prev => ({ ...prev, department_id: id, department: name, sub_department_id: '' }))}
              onSubDepartmentChange={(id) => setForm(prev => ({ ...prev, sub_department_id: id }))}
              deptError={errors.department}
              fieldSx={fieldSx}
              menuProps={selectMenuProps}
            />
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Manager / Supervisor" value={form.manager_supervisor} onChange={e => setForm({ ...form, manager_supervisor: e.target.value })} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Source of Injury" value={form.source_of_injury} onChange={e => setForm({ ...form, source_of_injury: e.target.value })} sx={fieldSx} SelectProps={{ MenuProps: selectMenuProps }}>
                {SOURCES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Location / Work Area" value={form.location_work_area} onChange={e => setForm({ ...form, location_work_area: e.target.value })} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Part of Body Injured" value={form.part_of_body_injured} onChange={e => setForm({ ...form, part_of_body_injured: e.target.value })} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nature of Injury" value={form.nature_of_injury} onChange={e => setForm({ ...form, nature_of_injury: e.target.value })} error={!!errors.nature_of_injury} helperText={errors.nature_of_injury} FormHelperTextProps={{ sx: { color: '#f87171' } }} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Days Lost" value={form.days_lost} onChange={e => setForm({ ...form, days_lost: e.target.value })} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Medical Treatment" value={form.medical_treatment} onChange={e => setForm({ ...form, medical_treatment: e.target.value })} sx={fieldSx} SelectProps={{ MenuProps: selectMenuProps }}>
                {MEDICAL_TYPES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Description of Events" value={form.description_of_events} onChange={e => setForm({ ...form, description_of_events: e.target.value })} error={!!errors.description_of_events} helperText={errors.description_of_events} FormHelperTextProps={{ sx: { color: '#f87171' } }} sx={fieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Corrective Action Taken or Recommended" value={form.corrective_action} onChange={e => setForm({ ...form, corrective_action: e.target.value })} error={!!errors.corrective_action} helperText={errors.corrective_action} FormHelperTextProps={{ sx: { color: '#f87171' } }} sx={fieldSx} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}
            sx={{ bgcolor: '#3b82f6', textTransform: 'none', fontWeight: 'bold',
              '&.Mui-disabled': { bgcolor: '#3b82f6', color: 'white', opacity: 0.8 } }}>
            {saving ? 'Saving...' : (editTarget ? 'Update Record' : 'Save Record')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { background: '#1e293b', color: 'white', borderRadius: '18px' } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Delete Record</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteTarget?.iod_number}</strong> — <em>{deleteTarget?.name_of_injured}</em>?</Typography>
          <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 1 }}>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={remove} disabled={deleting}
            sx={{ bgcolor: '#ef4444', textTransform: 'none', fontWeight: 'bold',
              '&.Mui-disabled': { bgcolor: '#ef4444', color: 'white', opacity: 0.8 } }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={notice.open} autoHideDuration={4000} onClose={() => setNotice({ ...notice, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={notice.severity} variant="filled" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#fff',
          bgcolor: notice.severity === 'error' ? '#ef4444' : '#10b981', '& .MuiAlert-icon': { color: '#fff' } }}>
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SHEAccidentRegister;
